import {
  ApiError,
  createEvent,
  createMessage,
  listSenders,
  listTemplates,
  llmIncidentDraft,
  llmMaintenanceDraft,
  llmMaintenances,
  llmStatus,
  type DraftAudience,
  type EventType,
  type IncidentDraft,
  type MaintenanceDraft,
  type MaintenanceList,
  type MaintenanceMode,
  type MessagePhase,
  type Sender,
  type Template,
} from "@/api";
import { Button } from "@/components/ui/Button";
import { fillPlaceholders } from "@/lib/format";
import { EMPTY_PLACEHOLDERS, defaultPhaseForType } from "@/lib/labels";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const KIND: { type: EventType; label: string; hint: string }[] = [
  { type: "info", label: "Einmalige Mail", hint: "Der Normalfall. Nach dem Senden erledigt." },
  { type: "incident", label: "Störung", hint: "Bleibt offen für Updates." },
  { type: "maintenance", label: "Wartung", hint: "Bleibt offen über Ankündigung bis Abschluss." },
];

type Fetch<T> = { phase: "idle" } | { phase: "loading" } | { phase: "error"; message: string } | { phase: "done"; data: T };

type IncidentState = Fetch<IncidentDraft>;

/** What "übernehmen" puts into the new mail. */
type Adoption = {
  draft: IncidentDraft["draft"];
  audience: DraftAudience;
  placeholders: Record<string, string>;
  phase: MessagePhase;
};

const MODE_LABEL: Record<MaintenanceMode, { label: string; hint: string }> = {
  redundancy: {
    label: "Nur eingeschränkte Redundanz",
    hint: "Geräte werden nacheinander gewartet, der Betrieb läuft weiter.",
  },
  outage: {
    label: "Ausfall",
    hint: "Alle Geräte der Wartung sind gleichzeitig weg (schlimmster Fall).",
  },
};

function errorMessage(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback;
}

const WEEKDAY = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

function formatWindow(start: string | null, end: string | null) {
  if (!start) return "";
  const from = new Date(start);
  const day = (d: Date) =>
    `${WEEKDAY[d.getDay()]} ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
  const clock = (d: Date) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  if (!end) return `${day(from)}, ${clock(from)}`;
  const to = new Date(end);
  const sameDay = from.toDateString() === to.toDateString();
  return `${day(from)}, ${clock(from)} – ${sameDay ? "" : `${day(to)}, `}${clock(to)}`;
}

function IncidentPanel({
  state,
  adopt,
  onAdopt,
  onRetry,
  withTemplate,
}: {
  state: IncidentState;
  adopt: boolean;
  withTemplate: boolean;
  onAdopt: (value: boolean) => void;
  onRetry: () => void;
}) {
  if (state.phase === "idle") return null;
  if (state.phase === "loading") {
    return (
      <p className="text-[11px] text-muted">
        Frage die Status-Seite nach einer aktuellen Störung und schreibe einen Vorschlag…
      </p>
    );
  }
  if (state.phase === "error") {
    return (
      <p className="text-[11px] text-danger">
        {state.message}{" "}
        <button type="button" className="underline" onClick={onRetry}>
          Erneut versuchen
        </button>
      </p>
    );
  }
  const { data } = state;
  const time = data.checked_at.slice(11, 16);
  const findings = <Findings lines={data.findings} />;

  if (!data.incident) {
    return (
      <div className="space-y-1 rounded-md border border-hairline bg-surface-subtle px-2 py-1.5">
        <p className="text-[11px] text-muted">
          Laut Status-Seite liegt gerade keine Störung für Kunden vor{time ? ` (Stand ${time} Uhr)` : ""}.{" "}
          <button type="button" className="underline" onClick={onRetry}>
            Neu prüfen
          </button>
        </p>
        {findings}
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-danger bg-surface-subtle px-2 py-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-danger">Aktuelle Störung{time ? ` · Stand ${time} Uhr` : ""}</span>
        <button type="button" className="text-[11px] text-muted underline" onClick={onRetry}>
          Neu prüfen
        </button>
      </div>
      <ul className="list-disc pl-4 text-[12px]">
        {data.impacts.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {data.buildings.length ? (
        <p className="text-[11px]">
          <span className="text-muted">Betroffen:</span> {data.buildings.join(", ")}
        </p>
      ) : null}
      {data.placeholders?.start ? (
        <p className="text-[11px]">
          <span className="text-muted">Seit ca.:</span> {data.placeholders.start}
        </p>
      ) : null}
      <DraftPreview
        audience={data.audience}
        draft={data.draft}
        draftError={data.draft_error}
        values={data.draft?.placeholders ?? {}}
        adopt={adopt}
        onAdopt={onAdopt}
        withTemplate={withTemplate}
      />
      {findings}
    </div>
  );
}

function DraftPreview({
  audience,
  draft,
  draftError,
  values,
  adopt,
  onAdopt,
  withTemplate,
}: {
  audience: DraftAudience;
  draft: IncidentDraft["draft"];
  draftError: string | null;
  values: Record<string, string>;
  adopt: boolean;
  onAdopt: (value: boolean) => void;
  // A chosen template supplies the text; the proposal only lends values and audience.
  withTemplate: boolean;
}) {
  const audienceText = audience
    ? audience.all
      ? "Alle Wohnheime"
      : audience.shorts.join(", ")
    : "nicht automatisch bestimmbar — bitte im Editor wählen";
  return (
    <>
      <p className="text-[11px]">
        <span className="text-muted">Zielgruppe:</span> {audienceText}
      </p>
      {audience?.unmatched.length ? (
        <p className="text-[11px] text-amber">
          Keiner Zielgruppe zugeordnet: {audience.unmatched.join(", ")} — bitte im Editor ergänzen.
        </p>
      ) : null}
      {draft ? (
        <div
          className={`rounded-md border border-hairline bg-surface px-2 py-1.5${withTemplate ? " opacity-50" : ""}`}
          title={withTemplate ? "Der Text kommt aus der gewählten Vorlage." : undefined}
        >
          <p className="text-sm font-medium">{fillPlaceholders(draft.subject_de, values)}</p>
          <div
            className="prose-mail text-[12px]"
            dangerouslySetInnerHTML={{ __html: fillPlaceholders(draft.html_de, values) }}
          />
        </div>
      ) : draftError ? (
        <p className="text-[11px] text-amber">Kein Textvorschlag: {draftError}.</p>
      ) : null}
      <label className="flex items-center gap-2 text-[12px]">
        <input type="checkbox" checked={adopt} onChange={(e) => onAdopt(e.target.checked)} />
        {draft && !withTemplate
          ? "Text, Platzhalter und Zielgruppe übernehmen"
          : withTemplate
            ? "Platzhalter und Zielgruppe übernehmen (Text aus der Vorlage)"
            : "Platzhalter und Zielgruppe übernehmen"}
      </label>
    </>
  );
}

function Findings({ lines }: { lines: string[] }) {
  if (!lines.length) return null;
  return (
    <details className="text-[11px] text-muted">
      <summary className="cursor-pointer">Details aus dem Monitoring</summary>
      <ul className="mt-1 list-disc pl-4 font-mono text-[10px]">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </details>
  );
}

function MaintenancePanel({
  list,
  selectedId,
  onSelect,
  mode,
  onMode,
  draft,
  impact,
  onImpact,
  onWrite,
  adopt,
  onAdopt,
  onReload,
  withTemplate,
}: {
  list: Fetch<MaintenanceList>;
  selectedId: string;
  onSelect: (id: string) => void;
  mode: MaintenanceMode | null;
  onMode: (mode: MaintenanceMode) => void;
  draft: Fetch<MaintenanceDraft>;
  impact: string;
  onImpact: (value: string) => void;
  onWrite: () => void;
  adopt: boolean;
  onAdopt: (value: boolean) => void;
  onReload: () => void;
  withTemplate: boolean;
}) {
  if (list.phase === "idle") return null;
  if (list.phase === "loading") {
    return <p className="text-[11px] text-muted">Frage die Status-Seite nach geplanten Wartungen…</p>;
  }
  if (list.phase === "error") {
    return (
      <p className="text-[11px] text-danger">
        {list.message}{" "}
        <button type="button" className="underline" onClick={onReload}>
          Erneut versuchen
        </button>
      </p>
    );
  }
  const { items, days, message } = list.data;
  const selected = items.find((item) => item.id === selectedId) ?? null;
  const modes = selected ? (["redundancy", "outage"] as MaintenanceMode[]).filter((m) => selected.variants[m]) : [];
  const variant = selected && mode ? selected.variants[mode] : undefined;

  return (
    <div className="space-y-2 rounded-md border border-hairline bg-surface-subtle px-2 py-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium">Wartungen laut Status-Seite</span>
        <button type="button" className="text-[11px] text-muted underline" onClick={onReload}>
          Neu laden
        </button>
      </div>
      {message ? <p className="text-[11px] text-amber">{message}</p> : null}
      {items.length ? (
        <div className="space-y-1">
          {items.map((item) => (
            <label
              key={item.id}
              className="flex cursor-pointer items-start gap-2 rounded-md border border-hairline bg-surface px-2 py-1 has-[:checked]:border-accent"
            >
              <input
                type="radio"
                name="maintenance"
                className="mt-1"
                checked={item.id === selectedId}
                onChange={() => onSelect(item.id)}
              />
              <span className="min-w-0">
                <span className="block text-[12px] font-medium">
                  {item.name || "Wartung"}
                  {item.is_active ? <span className="ml-1 text-amber">· läuft</span> : null}
                </span>
                <span className="text-[11px] text-muted">
                  {formatWindow(item.start, item.end)}
                  {!item.default_mode
                    ? " · keine Auswirkung für Kunden erkennbar"
                    : item.variants[item.default_mode]?.impact_known === false
                      ? " · Auswirkung unbekannt"
                      : ""}
                </span>
              </span>
            </label>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-muted">
          In den nächsten {days} Tagen ist keine Wartung eingetragen. Ohne Auswahl geht es mit Vorlage oder leerem
          Entwurf weiter.
        </p>
      )}
      {selected ? (
        <div className="space-y-2 border-t border-hairline pt-2">
          {selected.description ? <p className="text-[11px] text-muted">{selected.description}</p> : null}
          {modes.length > 1 ? (
            <div className="space-y-1">
              <span className="text-[11px] text-muted">Was bekommen die Bewohner mit?</span>
              {modes.map((m) => (
                <label key={m} className="flex cursor-pointer items-start gap-2 text-[12px]">
                  <input
                    type="radio"
                    name="maintenance-mode"
                    className="mt-0.5"
                    checked={mode === m}
                    onChange={() => onMode(m)}
                  />
                  <span>
                    {MODE_LABEL[m].label}
                    <span className="block text-[11px] text-muted">{MODE_LABEL[m].hint}</span>
                  </span>
                </label>
              ))}
            </div>
          ) : null}
          {variant ? (
            <>
              {variant.impact_known ? (
                <ul className="list-disc pl-4 text-[12px]">
                  {variant.impacts.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-[11px] text-amber">
                  Die Status-Seite ordnet die betroffenen Dienste keiner Netzebene zu — was Bewohner merken, bitte
                  selbst angeben.
                </p>
              )}
              <div className="space-y-1">
                <label className="field-label" htmlFor="maintenance-impact">
                  {variant.impact_known ? "Auswirkung für Bewohner (optional, ersetzt die Angaben oben)" : "Auswirkung für Bewohner"}
                </label>
                <textarea
                  id="maintenance-impact"
                  className="field min-h-[56px] text-[12px]"
                  maxLength={500}
                  placeholder="z. B. Versand und Abruf von E-Mails sind eingeschränkt."
                  value={impact}
                  onChange={(e) => onImpact(e.target.value)}
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={!impact.trim() || draft.phase === "loading"}
                    onClick={onWrite}
                  >
                    Vorschlag mit dieser Auswirkung
                  </Button>
                </div>
              </div>
              {variant.buildings.length ? (
                <p className="text-[11px]">
                  <span className="text-muted">Betroffen:</span> {variant.buildings.join(", ")}
                </p>
              ) : null}
            </>
          ) : null}
          {draft.phase === "loading" ? (
            <p className="text-[11px] text-muted">Schreibe einen Vorschlag…</p>
          ) : draft.phase === "error" ? (
            <p className="text-[11px] text-danger">{draft.message}</p>
          ) : draft.phase === "done" ? (
            <DraftPreview
              audience={draft.data.audience}
              draft={draft.data.draft}
              draftError={draft.data.draft_error}
              values={draft.data.placeholders}
              adopt={adopt}
              onAdopt={onAdopt}
              withTemplate={withTemplate}
            />
          ) : null}
          <Findings lines={selected.findings} />
        </div>
      ) : null}
    </div>
  );
}

export function NewEventPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const presetTemplateId = (location.state as { templateId?: string } | null)?.templateId ?? "";
  const [type, setType] = useState<EventType>("info");
  const [title, setTitle] = useState("");
  const [senderId, setSenderId] = useState("");
  const [senders, setSenders] = useState<Sender[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateId, setTemplateId] = useState(presetTemplateId);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [statusFeed, setStatusFeed] = useState(false);
  const [incident, setIncident] = useState<IncidentState>({ phase: "idle" });
  const [maintenances, setMaintenances] = useState<Fetch<MaintenanceList>>({ phase: "idle" });
  const [maintenanceId, setMaintenanceId] = useState("");
  const [maintenanceMode, setMaintenanceMode] = useState<MaintenanceMode | null>(null);
  const [maintenanceDraft, setMaintenanceDraft] = useState<Fetch<MaintenanceDraft>>({ phase: "idle" });
  const [maintenanceImpact, setMaintenanceImpact] = useState("");
  const draftRequest = useRef(0);
  const [adopt, setAdopt] = useState(true);

  useEffect(() => {
    let cancelled = false;
    llmStatus()
      .then((status) => {
        if (!cancelled) setStatusFeed(Boolean(status.status_feed));
      })
      .catch(() => {
        /* optional */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const checkIncident = useCallback(() => {
    setIncident({ phase: "loading" });
    llmIncidentDraft()
      .then((data) => {
        setIncident({ phase: "done", data });
        setAdopt(true);
      })
      .catch((err) => {
        setIncident({ phase: "error", message: errorMessage(err, "Status-Seite konnte nicht abgefragt werden.") });
      });
  }, []);

  // Choosing "Störung" asks the status page once; "Neu prüfen" repeats it.
  useEffect(() => {
    if (type === "incident" && statusFeed && incident.phase === "idle") checkIncident();
  }, [type, statusFeed, incident.phase, checkIncident]);

  const loadMaintenances = useCallback(() => {
    draftRequest.current += 1;
    setMaintenances({ phase: "loading" });
    setMaintenanceId("");
    setMaintenanceImpact("");
    setMaintenanceDraft({ phase: "idle" });
    llmMaintenances()
      .then((data) => setMaintenances({ phase: "done", data }))
      .catch((err) => {
        setMaintenances({ phase: "error", message: errorMessage(err, "Status-Seite konnte nicht abgefragt werden.") });
      });
  }, []);

  // Choosing "Wartung" lists the planned maintenances once; "Neu laden" repeats it.
  useEffect(() => {
    if (type === "maintenance" && statusFeed && maintenances.phase === "idle") loadMaintenances();
  }, [type, statusFeed, maintenances.phase, loadMaintenances]);

  // Only the selected maintenance goes to the LLM, again on every mode switch.
  const draftMaintenance = useCallback((id: string, mode: MaintenanceMode | null, impact = "") => {
    const request = ++draftRequest.current;
    setMaintenanceId(id);
    setMaintenanceMode(mode);
    if (!mode) {
      setMaintenanceDraft({ phase: "idle" });
      return;
    }
    setMaintenanceDraft({ phase: "loading" });
    llmMaintenanceDraft(id, mode, impact.trim())
      .then((data) => {
        if (request !== draftRequest.current) return;
        setMaintenanceDraft({ phase: "done", data });
        setAdopt(true);
      })
      .catch((err) => {
        if (request !== draftRequest.current) return;
        setMaintenanceDraft({ phase: "error", message: errorMessage(err, "Kein Vorschlag möglich.") });
      });
  }, []);

  const selectMaintenance = useCallback(
    (id: string) => {
      const item = maintenances.phase === "done" ? maintenances.data.items.find((m) => m.id === id) : undefined;
      setMaintenanceImpact("");
      draftMaintenance(id, item?.default_mode ?? null);
    },
    [maintenances, draftMaintenance],
  );

  const adoption = useMemo<Adoption | null>(() => {
    if (!adopt) return null;
    if (type === "incident" && incident.phase === "done" && incident.data.incident) {
      const data = incident.data;
      return {
        draft: data.draft,
        audience: data.audience,
        placeholders: data.draft?.placeholders ?? data.placeholders ?? { building: data.buildings.join(", ") },
        phase: "start",
      };
    }
    if (type === "maintenance" && maintenanceDraft.phase === "done" && maintenanceDraft.data.mode) {
      const data = maintenanceDraft.data;
      return {
        draft: data.draft,
        audience: data.audience,
        placeholders: data.placeholders,
        phase: data.maintenance.is_active ? "start" : "announcement",
      };
    }
    return null;
  }, [adopt, type, incident, maintenanceDraft]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listSenders(), listTemplates()])
      .then(([senderItems, templateItems]) => {
        if (cancelled) return;
        setSenders(senderItems);
        setTemplates(templateItems);
        setSenderId((current) => current || senderItems[0]?.id || "");
      })
      .catch(() => {
        /* optional */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const matchingTemplates = useMemo(
    () => templates.filter((item) => !item.event_type || item.event_type === type),
    [templates, type],
  );

  useEffect(() => {
    if (!templateId) return;
    const selected = templates.find((item) => item.id === templateId);
    if (selected?.event_type) setType(selected.event_type);
  }, [templateId, templates]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const selected = templates.find((item) => item.id === templateId);
      // A chosen template wins over the proposed text, not over its values and audience.
      const draft = templateId ? null : (adoption?.draft ?? null);
      const foundValues = adoption?.placeholders ?? {};
      const audience = adoption?.audience ?? null;
      const subject = draft?.subject_de ?? (adoption ? selected?.subject_de : "") ?? "";
      const filledSubject = fillPlaceholders(subject, foundValues);
      const draftTitle = filledSubject.includes("{{") ? "" : filledSubject;
      const created = await createEvent({
        type,
        title: title.trim() || draftTitle || selected?.title || "Neue Mail",
        // Values are filled in the editor rail, next to the text that uses them.
        placeholders: { ...EMPTY_PLACEHOLDERS, ...foundValues },
        language_policy: "bilingual",
        sender_id: senderId || null,
        ...(audience
          ? { audience_filter: { source: audience.source, rules: audience.shorts } }
          : {}),
      });
      await createMessage(
        created.id,
        draft
          ? {
              phase: adoption?.phase ?? defaultPhaseForType(type),
              subject_de: draft.subject_de,
              subject_en: "",
              html_de: draft.html_de,
              html_en: "",
              text_de: "",
              text_en: "",
            }
          : templateId
          ? {
              template_id: templateId,
              subject_de: "",
              subject_en: "",
              html_de: "",
              html_en: "",
              text_de: "",
              text_en: "",
            }
          : {
              phase: adoption?.phase ?? defaultPhaseForType(type),
              subject_de: "",
              subject_en: "",
              html_de: "",
              html_en: "",
              text_de: "",
              text_en: "",
            },
      );
      navigate(`/events/${created.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mail konnte nicht angelegt werden.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-xl px-4 py-4">
      <h1 className="mb-3 text-base font-semibold">Neue Mail</h1>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 rounded-lg border border-hairline bg-surface p-4">
        <div>
          <span className="field-label">Was ist das?</span>
          <div className="space-y-1.5">
            {KIND.map((item) => (
              <label
                key={item.type}
                className="flex cursor-pointer items-start gap-2 rounded-md border border-hairline px-2 py-1.5 has-[:checked]:border-accent has-[:checked]:bg-accent-dim"
              >
                <input
                  type="radio"
                  name="type"
                  className="mt-1"
                  value={item.type}
                  checked={type === item.type}
                  onChange={() => setType(item.type)}
                />
                <span>
                  <span className="block text-sm font-medium">{item.label}</span>
                  <span className="text-[11px] text-muted">{item.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
        {type === "incident" ? (
          <IncidentPanel
            state={incident}
            adopt={adopt}
            onAdopt={setAdopt}
            onRetry={checkIncident}
            withTemplate={Boolean(templateId)}
          />
        ) : null}
        {type === "maintenance" ? (
          <MaintenancePanel
            list={maintenances}
            selectedId={maintenanceId}
            onSelect={selectMaintenance}
            mode={maintenanceMode}
            onMode={(mode) => draftMaintenance(maintenanceId, mode, maintenanceImpact)}
            draft={maintenanceDraft}
            impact={maintenanceImpact}
            onImpact={setMaintenanceImpact}
            onWrite={() => draftMaintenance(maintenanceId, maintenanceMode, maintenanceImpact)}
            adopt={adopt}
            onAdopt={setAdopt}
            onReload={loadMaintenances}
            withTemplate={Boolean(templateId)}
          />
        ) : null}
        <div>
          <label className="field-label" htmlFor="title">
            Interner Titel (optional)
          </label>
          <input
            id="title"
            className="field"
            placeholder="Wird aus der Vorlage oder dem Betreff ergänzt"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="template">
            Vorlage
          </label>
          <select
            id="template"
            className="field"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            <option value="">{adoption?.draft ? "Textvorschlag von oben" : "Leerer Entwurf"}</option>
            {matchingTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.title}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor="sender">
            Absender
          </label>
          <select id="sender" className="field" value={senderId} onChange={(e) => setSenderId(e.target.value)}>
            <option value="">— keiner —</option>
            {senders.map((sender) => (
              <option key={sender.id} value={sender.id}>
                {sender.display_name} &lt;{sender.header_from}&gt;
              </option>
            ))}
          </select>
        </div>
        {error ? <p className="text-sm text-danger">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => navigate("/")}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? "Öffnen…" : "Weiter zum Editor"}
          </Button>
        </div>
      </form>
    </div>
  );
}
